import React from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, formatNumber, formatDate, tooltipStyle } from '../types';

interface DevicesTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = ['device-analytics'];

const RankTable = ({ rows, labelKey, title }: { rows: any[]; labelKey: string; title: string }) => (
  <ChartCard title={title}>
    <div className="max-h-80 overflow-y-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-gray-400 border-b border-white/10">
          <th className="pb-2 font-medium w-10">#</th>
          <th className="pb-2 font-medium">{labelKey === 'version' ? 'Version' : labelKey === 'model' ? 'Device' : 'OS Version'}</th>
          {labelKey === 'model' && <th className="pb-2 font-medium">Platform</th>}
          <th className="pb-2 font-medium text-right">Users</th>
        </tr></thead>
        <tbody>
          {rows.map((r: any, i: number) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/5">
              <td className="py-2 text-gray-500">{i + 1}</td>
              <td className="py-2 text-white font-mono text-xs">{r[labelKey]}</td>
              {labelKey === 'model' && <td className="py-2 text-gray-400 text-xs">{r.platform}</td>}
              <td className="py-2 text-right text-white font-semibold">{formatNumber(r.count)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-gray-500">No data yet</td></tr>}
        </tbody>
      </table>
    </div>
  </ChartCard>
);

const DevicesTab: React.FC<DevicesTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  if (loading) return <LoadingSpinner />;

  const da = data['device-analytics'] || {};
  const appVersions = da.appVersions || [];
  const androidOsVersions = da.androidOsVersions || [];
  const iosOsVersions = da.iosOsVersions || [];
  const topDeviceModels = da.topDeviceModels || [];
  const androidAppVersions = da.androidAppVersions || [];
  const iosAppVersions = da.iosAppVersions || [];
  const dailyPlatform = da.dailyPlatform || [];

  const totalAndroid = dailyPlatform.reduce((s: number, d: any) => s + (d.Android || 0), 0);
  const totalIos = dailyPlatform.reduce((s: number, d: any) => s + (d.iOS || 0), 0);

  return (
    <div className="space-y-6">
      {/* Platform trend */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Android Signups (30d)" value={totalAndroid} />
        <StatCard title="iOS Signups (30d)" value={totalIos} />
        <StatCard title="Android %" value={totalAndroid + totalIos > 0 ? `${((totalAndroid / (totalAndroid + totalIos)) * 100).toFixed(1)}%` : '\u2014'} />
        <StatCard title="iOS %" value={totalAndroid + totalIos > 0 ? `${((totalIos / (totalAndroid + totalIos)) * 100).toFixed(1)}%` : '\u2014'} />
      </div>

      {dailyPlatform.length > 0 && (
        <ChartCard title="Daily Signups by Platform (Last 30 Days)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyPlatform}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={formatDate} />
              <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              <Bar dataKey="Android" fill="#34d399" stackId="a" />
              <Bar dataKey="iOS" fill="#38bdf8" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* App Versions - Android vs iOS side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RankTable rows={androidAppVersions} labelKey="version" title="App Versions (Android)" />
        <RankTable rows={iosAppVersions} labelKey="version" title="App Versions (iOS)" />
      </div>

      {/* OS Versions - Android vs iOS side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RankTable rows={androidOsVersions} labelKey="osVersion" title="OS Versions (Android)" />
        <RankTable rows={iosOsVersions} labelKey="osVersion" title="OS Versions (iOS)" />
      </div>

      {/* Overall app versions chart + top devices */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {appVersions.length > 0 && (
          <ChartCard title="Top App Versions (All Platforms)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={appVersions.slice(0, 15)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                <YAxis dataKey="version" type="category" width={120} stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#FF9C15" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
        <RankTable rows={topDeviceModels} labelKey="model" title="Top Device Models" />
      </div>
    </div>
  );
};

export default DevicesTab;
